import type { AppointmentStatus } from "@planerius/contracts"

/**
 * Шесть статусов записи и то, чем они различаются.
 *
 * ГЛАВНОЕ ПРАВИЛО РАЗДЕЛА (ТЗ §2.3.3): статус различим НЕ ЦВЕТОМ. Причин три, и ни одна
 * не про вкус: администратор может быть дальтоником; салон печатает день на чёрно-белом
 * принтере и вешает у стойки; цвет в колонке уже занят — он принадлежит мастеру, и
 * кодировать статус поверх цвета мастера значит сделать нечитаемым и то и другое.
 *
 * Поэтому здесь описаны ЧЕТЫРЕ канала различения, и цвет в их число не входит:
 *   1) стиль границы — пунктир против сплошной; толщина в канал не входит, её на
 *      опорном экране не рисуют (см. BORDER_ON_SCREEN),
 *   2) заливка (белая, сплошная серая, редкая штриховка, плотная штриховка),
 *   3) значок в фиксированном левом углу,
 *   4) левая полоса и зачёркивание имени.
 * Цвет добавляет CSS поверх, четвёртым по счёту.
 *
 * Проверка status-style.test.ts требует, чтобы ЛЮБЫЕ два статуса различались минимум
 * в двух каналах. Это машинная формулировка критерия приёмки «макет в градациях серого
 * остаётся полностью читаемым»: одного различия мало — оно может потеряться в карточке
 * высотой 20 px, где значок виден, а границы почти нет.
 *
 * ЗАЛИВКА СЧИТАЕТСЯ КАНАЛОМ НЕ ПО НАЗВАНИЮ. Имена hatch-sparse и hatch-dense различны
 * всегда — а вот покрашены они могут оказаться одинаково, как и было до правок дизайнера:
 * один угол, один шаг, разные цвета, и в градациях серого одна заливка на два статуса.
 * Поэтому этот модуль НЕ утверждает, что заливки разные, а только называет токен, которым
 * каждая нарисована; сравнивает их проверка, читая настоящие значения из tokens.css.
 */

export type StatusBorder = "solid" | "dashed" | "thick" | "thick-dashed"
export type StatusFill = "plain" | "solid" | "hatch-sparse" | "hatch-dense"

/**
 * Как граница ВЫГЛЯДИТ на опорном экране, а не как она названа.
 *
 * Толщина 1.5 px пришла из макета («не пришёл» — 1.5px solid, «ожидает» — 1.5px dashed)
 * и на ретине и на печати видна. Но администратор сидит за 1366×768 (ТЗ §2.0), а там
 * плотность пикселей единичная, и браузер округляет границу до целого устройственного
 * пикселя: обе толщины приходят к 1 px. Проверено в Chrome на настоящих токенах —
 * getComputedStyle отдаёт 1px, и коробка карточки это подтверждает: offsetHeight −
 * clientHeight = 2 при обеих объявленных толщинах.
 *
 * Значит НА ОПОРНОМ ЭКРАНЕ толщина признаком не является, и считать её каналом — ровно
 * та же ошибка, какой была заливка до правок дизайнера: различие по названию, которого
 * на экране нет. Поэтому канал границы называет только СТИЛЬ. Толщина остаётся в макете
 * и работает там, где её видно, но приёмка «читаемо в градациях серого» на неё не
 * опирается — и без неё все пятнадцать пар всё равно расходятся минимум в двух каналах.
 */
const BORDER_ON_SCREEN: Readonly<Record<StatusBorder, string>> = {
  solid: "solid",
  dashed: "dashed",
  thick: "solid",
  "thick-dashed": "dashed",
}

export const ALL_FILLS: readonly StatusFill[] = ["plain", "solid", "hatch-sparse", "hatch-dense"]

/**
 * Чем именно нарисована заливка. Здесь имя переменной, а не цвет: цвета в коде не живут
 * (правило слоя токенов), а проверке нужно знать, куда смотреть в tokens.css.
 */
export const FILL_TOKEN: Readonly<Record<StatusFill, string>> = {
  plain: "--bg-card",
  solid: "--status-completed-bg",
  "hatch-sparse": "--status-cancelled-hatch",
  "hatch-dense": "--status-no-show-hatch",
}

export interface StatusVisual {
  readonly status: AppointmentStatus
  /** Название из единого словаря раздела 1 §5.5. Здесь оно не переизобретается. */
  readonly name: string
  /** Короткая метка на карточке. У «подтверждена» её нет: это фон для остальных. */
  readonly label: string
  readonly glyph: string
  readonly border: StatusBorder
  readonly fill: StatusFill
  /** Толстая левая полоса. Есть только у «в работе» — того, что происходит прямо сейчас. */
  readonly hasBar: boolean
  readonly strikeName: boolean
  /** Имя приглушено: визит уже позади и внимания не требует. */
  readonly dimName: boolean
  readonly faded: boolean
}

const VISUALS: Readonly<Record<AppointmentStatus, StatusVisual>> = {
  pending: {
    status: "pending",
    name: "Ожидает подтверждения",
    label: "Ждёт подтверждения",
    glyph: "?",
    border: "thick-dashed",
    fill: "plain",
    hasBar: false,
    strikeName: false,
    dimName: false,
    faded: false,
  },
  confirmed: {
    status: "confirmed",
    name: "Подтверждена",
    label: "",
    glyph: "",
    border: "solid",
    fill: "plain",
    hasBar: false,
    strikeName: false,
    dimName: false,
    faded: false,
  },
  in_progress: {
    status: "in_progress",
    name: "В работе",
    label: "В работе",
    glyph: "▸",
    border: "solid",
    fill: "plain",
    hasBar: true,
    strikeName: false,
    dimName: false,
    faded: false,
  },
  completed: {
    status: "completed",
    name: "Завершён",
    label: "Завершён",
    glyph: "✓",
    border: "solid",
    fill: "solid",
    hasBar: false,
    strikeName: false,
    dimName: true,
    faded: false,
  },
  cancelled: {
    status: "cancelled",
    name: "Отменён",
    label: "Отменён",
    glyph: "✕",
    border: "dashed",
    fill: "hatch-sparse",
    hasBar: false,
    strikeName: true,
    dimName: false,
    // Отменённая запись показана по фильтру и обязана не мешать увидеть, что время
    // свободно (ТЗ §2.3.3). Полупрозрачность — то, чем она уходит на второй план.
    faded: true,
  },
  no_show: {
    status: "no_show",
    name: "Не пришёл",
    label: "Не пришёл",
    glyph: "—",
    border: "thick",
    fill: "hatch-dense",
    hasBar: false,
    strikeName: false,
    dimName: false,
    faded: false,
  },
}

export function statusVisual(status: AppointmentStatus): StatusVisual {
  return VISUALS[status]
}

export const ALL_STATUSES: readonly AppointmentStatus[] = [
  "pending",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
  "no_show",
]

/**
 * Каналы различения одного статуса в машинном виде. Нужны проверке: она сравнивает
 * статусы попарно и требует расхождения минимум в двух каналах.
 *
 * @param fillSignature как ВЫГЛЯДИТ заливка этого статуса после обесцвечивания. Приходит
 * снаружи, а не берётся из visual.fill, и это главное здесь: название заливки ничего
 * не обещает о том, чем она нарисована. Подпись считает тот, кто видит настоящие значения
 * токенов, — и две заливки, различающиеся только цветом, получают одну и ту же подпись,
 * то есть перестают быть каналом.
 */
export function distinctionChannels(
  visual: StatusVisual,
  fillSignature: string,
): readonly string[] {
  return [
    // Стиль, а не толщина: см. BORDER_ON_SCREEN — на 1366×768 толщина не рисуется.
    `border:${BORDER_ON_SCREEN[visual.border]}`,
    `fill:${fillSignature}`,
    `glyph:${visual.glyph}`,
    `bar:${String(visual.hasBar)}`,
    `strike:${String(visual.strikeName)}`,
  ]
}
